import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';

import { Box, Chip, Divider, TextField } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import Autocomplete from '@material-ui/lab/Autocomplete';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useDebounce } from 'use-debounce';

import {
  AutoCompleteRemoto,
  DialogoConfirmacao,
  SwitchInput,
} from '../../Components';
import { api, createAPI, getUserId } from '../../services';
import {
  formatMoeda,
  formatPeso,
  formatCNPJ,
  STATUS_ATIVO,
  SECONDARY_COLOR,
} from '../../utils';
import DialogoPermissaoIsento from './DialogoPermissaoIsento';
import { nomeFornecedor } from './FornecedorField';

// 1 = Fornecedor Padrão, 2 = Importação (fornecedores do sistema, sem CNPJ de NF-e)
const FORNECEDORES_EXCLUIDOS = [1, 2];

const ETAPA_FORNECEDOR = 1;
const ETAPA_NFE = 2;
const ETAPA_ISENTO = 3;

const TAMANHO_NUMERO = 9; // o número da NF-e ocupa 9 posições na chave
const MIN_DIGITOS_CHAVE = 6;
const TAMANHO_CHAVE = 44;

const CAMPOS_FORNECEDOR = [
  { label: 'Responsável', campo: 'nome' },
  { label: 'Nome Fantasia', campo: 'nome_fantasia' },
  { label: 'Razão Social', campo: 'razao_social' },
  { label: 'CNPJ', campo: 'cnpj' },
];

const useStyles = makeStyles(() => ({
  option: {
    display: 'block',
    padding: '10px 16px',
    borderBottom: '1px solid #ddd',
    '&:last-child': { borderBottom: 'none' },
  },
  listbox: { maxHeight: '45vh' },
}));

const mensagemDoErro = (erro, padrao) =>
  erro && erro.response && erro.response.data && erro.response.data.message
    ? erro.response.data.message
    : padrao;

function OpcaoNfe({ nfe, fornecedor }) {
  const campo = (rotulo, valor, extra = {}) => (
    <Box display="flex" style={{ gap: '8px' }}>
      <span style={{ color: '#777', minWidth: '70px' }}>{rotulo}</span>
      <span style={extra}>{valor}</span>
    </Box>
  );
  return (
    <Box width="100%">
      <Box
        display="flex"
        justifyContent="space-between"
        fontWeight="bold"
        fontSize="16px"
        marginBottom="4px"
      >
        <span>{`NF-e ${nfe.numero}`}</span>
        <span>{formatMoeda(nfe.vNF)}</span>
      </Box>
      {campo(
        'Fornecedor',
        nomeFornecedor({ razao_social: nfe.emitRazao, ...fornecedor }),
      )}
      {campo('Emissão', moment(nfe.dhEmis).format('DD/MM/YYYY'))}
      {campo('Chave', nfe.chave, {
        fontFamily: 'monospace',
        letterSpacing: '0.5px',
      })}
    </Box>
  );
}

// Cria o descarrego de UMA NF-e: pendente (será pago no caixa, F1) ou isento (já nasce pago).
// Etapa 1: fornecedor (ou, sem fornecedor, chave de acesso / número da NF-e). Etapa 2: NF-e,
// resumo (peso, frete e valor calculados no servidor) e isento.
const DialogoCreate = forwardRef(({ handleClose = () => {} }, ref) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [etapa, setEtapa] = useState(ETAPA_FORNECEDOR);
  const [fornecedor, setFornecedor] = useState(null);
  const [nfesFornecedor, setNfesFornecedor] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [chaveDigitada, setChaveDigitada] = useState('');
  const [sugestoes, setSugestoes] = useState([]);
  const [semResultado, setSemResultado] = useState('');
  const [previa, setPrevia] = useState(null);
  const [erro, setErro] = useState('');
  const [isento, setIsento] = useState(false);
  const [autorizacao, setAutorizacao] = useState(null);
  const refPermissaoIsento = useRef(null);
  const refConfirmacao = useRef(null);
  const refConsulta = useRef(0); // descarta respostas antigas
  const [chaveDebounce] = useDebounce(chaveDigitada, 400);

  function limpar() {
    refConsulta.current += 1;
    setEtapa(ETAPA_FORNECEDOR);
    setFornecedor(null);
    setNfesFornecedor([]);
    setCarregando(false);
    setChaveDigitada('');
    setSugestoes([]);
    setSemResultado('');
    setPrevia(null);
    setErro('');
    setIsento(false);
    setAutorizacao(null);
  }

  useImperativeHandle(ref, () => ({
    handleOpen() {
      limpar();
      setOpen(true);
    },
  }));

  function fechar() {
    setOpen(false);
    handleClose();
  }

  async function buscarPrevia(chave) {
    const consulta = refConsulta.current + 1;
    refConsulta.current = consulta;
    setPrevia(null);
    setErro('');
    try {
      const response = await api.get(`/descarrego_mercadorias/previa/${chave}`);
      if (consulta !== refConsulta.current) return;
      const { status, message, data } = response.data;
      if (status) setPrevia(data);
      else setErro(message);
    } catch (e) {
      if (consulta !== refConsulta.current) return;
      setErro(
        mensagemDoErro(
          e,
          'Não foi possível consultar a NF-e. Verifique a conexão.',
        ),
      );
    }
  }

  // etapa 1 -> 2: lista as NF-es elegíveis pelo CNPJ do fornecedor escolhido
  async function irParaEtapaNfe() {
    setChaveDigitada('');
    setSugestoes([]);
    if (!fornecedor) {
      // sem fornecedor: a NF-e é achada pela chave ou pelo número
      setNfesFornecedor([]);
      setPrevia(null);
      setErro('');
      setEtapa(ETAPA_NFE);
      return;
    }
    const cnpj = (fornecedor.cnpj || '').replace(/\D/g, '');
    if (!cnpj) {
      setErro(
        'Este fornecedor não possui CNPJ cadastrado, por isso não é possível localizar NF-es.',
      );
      return;
    }
    const consulta = refConsulta.current + 1;
    refConsulta.current = consulta;
    setErro('');
    setCarregando(true);
    try {
      const response = await api.get('/descarrego_mercadorias/nfes-elegiveis', {
        params: { cnpj },
      });
      if (consulta !== refConsulta.current) return;
      const { status, message, data } = response.data;
      if (!status) setErro(message);
      else if (data.length === 0)
        setErro('Nenhuma NF-e recebida e sem descarrego para este fornecedor.');
      else {
        setNfesFornecedor(data);
        setPrevia(null);
        setEtapa(ETAPA_NFE);
      }
    } catch (e) {
      if (consulta === refConsulta.current) {
        setErro(
          'Não foi possível listar as NF-es do fornecedor. Verifique a conexão.',
        );
      }
    } finally {
      if (consulta === refConsulta.current) setCarregando(false);
    }
  }

  function voltarParaEtapaFornecedor() {
    refConsulta.current += 1;
    setEtapa(ETAPA_FORNECEDOR);
    setChaveDigitada('');
    setSugestoes([]);
    setPrevia(null);
    setErro('');
    setIsento(false);
    setAutorizacao(null);
  }

  // fechar com algo já preenchido descarta tudo: pede confirmação
  function tentarFechar() {
    const temDados = !!(fornecedor || previa || chaveDigitada || isento);
    if (temDados && refConfirmacao.current) {
      refConfirmacao.current.handleOpen(
        'Descartar descarrego',
        'Há dados preenchidos neste descarrego. Se fechar agora eles serão perdidos. Deseja fechar?',
        'fechar-dialogo',
      );
    } else {
      fechar();
    }
  }

  function voltar() {
    if (etapa === ETAPA_ISENTO) {
      setErro('');
      setEtapa(ETAPA_NFE);
    } else if (etapa === ETAPA_NFE) voltarParaEtapaFornecedor();
    else tentarFechar();
  }

  function avancar() {
    if (etapa === ETAPA_FORNECEDOR) irParaEtapaNfe();
    else if (etapa === ETAPA_NFE) {
      if (previa) {
        setErro('');
        setEtapa(ETAPA_ISENTO);
      }
    } else handleSalvar();
  }

  // sugestões (etapa NF-e, sem fornecedor): por trecho da chave (6+ dígitos) e/ou pelo número da NF-e
  useEffect(() => {
    if (!open || etapa !== ETAPA_NFE || fornecedor) return undefined;
    const digitos = chaveDebounce.replace(/[\s.-]/g, '');
    if (!/^\d+$/.test(digitos) || digitos.length >= TAMANHO_CHAVE) {
      setSugestoes([]);
      return undefined;
    }
    const consultas = [];
    if (digitos.length >= MIN_DIGITOS_CHAVE) {
      consultas.push({ chave: digitos, filtro: null });
    }
    if (digitos.length <= TAMANHO_NUMERO) {
      // o servidor só busca trecho da chave: o número, com zeros à esquerda, é um trecho dela
      consultas.push({
        chave: digitos.padStart(TAMANHO_NUMERO, '0'),
        filtro: String(parseInt(digitos, 10)),
      });
    }
    let cancelado = false;
    Promise.all(
      consultas.map(({ chave, filtro }) =>
        api
          .get('/descarrego_mercadorias/nfes-elegiveis', { params: { chave } })
          .then((response) => {
            const { status, message, data } = response.data;
            if (!status) throw new Error(message);
            return filtro ? data.filter((n) => n.numero === filtro) : data;
          }),
      ),
    )
      .then((listas) => {
        if (cancelado) return;
        const vistos = new Set();
        const unidas = listas
          .flat()
          .filter((n) => !vistos.has(n.id) && vistos.add(n.id));
        setSugestoes(unidas);
        setSemResultado(unidas.length === 0 ? digitos : '');
      })
      .catch((e) => {
        if (cancelado) return;
        setSugestoes([]);
        setErro(
          e.message ||
            'Não foi possível consultar as NF-es. Verifique a conexão.',
        );
      });
    return () => {
      cancelado = true;
    };
  }, [chaveDebounce, open, etapa, fornecedor]);

  function getAjudaChave() {
    if (fornecedor) return '';
    const texto = chaveDigitada.replace(/[\s.-]/g, '');
    if (texto === '') return 'Digite o número da NF-e ou a chave de acesso';
    if (!/^\d+$/.test(texto)) return 'Use apenas números';
    if (semResultado === texto) {
      return 'Nenhuma NF-e recebida, em ciência e sem descarrego encontrada';
    }
    return '';
  }

  // com fornecedor, filtra a lista dele pelo que foi digitado; sem fornecedor, usa as sugestões do servidor
  const opcoesNfe = fornecedor
    ? nfesFornecedor.filter((n) =>
        `${n.chave} ${n.numero}`.includes(chaveDigitada),
      )
    : sugestoes;

  function processarTexto(texto) {
    const valor = (texto || '').trim();
    if (!valor) return;
    const chave = valor.replace(/[\s.-]/g, '');
    if (/^\d+$/.test(chave) && chave.length === TAMANHO_CHAVE) {
      buscarPrevia(chave);
    } else if (opcoesNfe.length === 1) {
      setChaveDigitada(opcoesNfe[0].chave);
      buscarPrevia(opcoesNfe[0].chave);
    } else {
      enqueueSnackbar(
        opcoesNfe.length > 1
          ? 'Mais de uma NF-e encontrada: escolha uma das opções.'
          : `Digite o número da NF-e ou a chave completa (${TAMANHO_CHAVE} dígitos).`,
        { variant: 'warning' },
      );
    }
  }

  async function handleSalvar() {
    if (!previa) return;

    if (isento && !autorizacao) {
      enqueueSnackbar('Autorize a isenção antes de salvar.', {
        variant: 'warning',
      });
      return;
    }

    setCarregando(true);
    try {
      await createAPI('descarrego_mercadorias', {
        nfe_compra_id: previa.nfe.id,
        isento,
        user_id: isento ? autorizacao.user.id : parseInt(getUserId(), 10),
        gerente_user_id: isento ? autorizacao.gerente.id : null,
        testemunha_user_id: isento ? autorizacao.testemunha.id : null,
      });
      enqueueSnackbar(
        isento
          ? 'Descarrego isento registrado.'
          : 'Descarrego criado. Aguardando pagamento no caixa.',
        { variant: 'success' },
      );
      fechar();
    } catch (e) {
      enqueueSnackbar(mensagemDoErro(e, 'Erro ao criar o descarrego.'), {
        variant: 'error',
      });
    } finally {
      setCarregando(false);
    }
  }

  const itemResumo = (rotulo, valor) => (
    <Box fontSize="15px" fontWeight="bold" display="flex">
      <span style={{ color: SECONDARY_COLOR, marginRight: '10px' }}>
        {rotulo}
      </span>
      {valor}
    </Box>
  );

  // na etapa da NF-e o quadro aparece sempre; sem NF-e escolhida mostra o fornecedor da etapa 1 e traços
  const resumo = etapa !== ETAPA_FORNECEDOR && (
    <Box
      display="flex"
      mt="10px"
      padding="10px"
      style={{ border: `1px solid ${SECONDARY_COLOR}`, borderRadius: '5px' }}
    >
      <Box flex={1} display="flex" flexDirection="column">
        {itemResumo('NF-e:', previa ? previa.nfe.numero : '-')}
        {itemResumo(
          'Fornecedor:',
          previa
            ? nomeFornecedor({
                razao_social: previa.nfe.emitRazao,
                ...previa.fornecedor,
              })
            : fornecedor
            ? nomeFornecedor(fornecedor)
            : '-',
        )}
        {itemResumo(
          'CNPJ:',
          previa || fornecedor
            ? formatCNPJ((previa ? previa.fornecedor : fornecedor).cnpj)
            : '-',
        )}
        {itemResumo(
          'Emissão:',
          previa ? moment(previa.nfe.dhEmis).format('DD/MM/YYYY') : '-',
        )}
      </Box>
      <Box flex={1} display="flex" flexDirection="column">
        {itemResumo(
          'Valor da NF-e:',
          previa ? formatMoeda(previa.nfe.vNF) : '-',
        )}
        {itemResumo('Peso:', previa ? `${formatPeso(previa.peso)} Kg` : '-')}
        {itemResumo(
          'Frete:',
          previa ? `${formatMoeda(previa.frete)} / t` : '-',
        )}
        {itemResumo('Descarrego:', previa ? formatMoeda(previa.valor) : '-')}
      </Box>
    </Box>
  );

  return (
    <>
      {/* keepMounted, como o DialogoPermissaoIsento: a ordem de montagem no DOM faz o de isento ficar por cima */}
      <Dialog
        open={open}
        onClose={tentarFechar}
        disableBackdropClick
        fullWidth
        maxWidth="md"
        keepMounted
      >
        <DialogTitle
          style={{
            color: 'white',
            backgroundColor: SECONDARY_COLOR,
            textAlign: 'center',
          }}
        >
          {etapa === ETAPA_FORNECEDOR && 'NOVO DESCARREGO - FORNECEDOR (1/3)'}
          {etapa === ETAPA_NFE && 'NOVO DESCARREGO - NF-e (2/3)'}
          {etapa === ETAPA_ISENTO && 'NOVO DESCARREGO - ISENÇÃO (3/3)'}
        </DialogTitle>
        <DialogContent>
          {etapa === ETAPA_FORNECEDOR && (
            <>
              <Box display="flex" flexWrap="wrap" mt="10px">
                {CAMPOS_FORNECEDOR.map(({ label, campo }, i) => (
                  <Box
                    key={campo}
                    flexBasis="50%"
                    boxSizing="border-box"
                    paddingRight={i % 2 === 0 ? '5px' : 0}
                    paddingLeft={i % 2 === 1 ? '5px' : 0}
                  >
                    <AutoCompleteRemoto
                      id={`fornecedor-${campo}`}
                      name={`fornecedor-${campo}`}
                      label={label}
                      value={fornecedor}
                      onChange={(novo) => {
                        setFornecedor(novo);
                        setErro('');
                      }}
                      handleKey={() => {}}
                      handleEnter={irParaEtapaNfe}
                      resource="fornecedores"
                      campo={campo}
                      campoPesquisa={campo}
                      toString={false}
                      autoFocus={i === 0}
                      fullwidth
                      filters={{
                        status: STATUS_ATIVO,
                        ids_excluso: FORNECEDORES_EXCLUIDOS,
                      }}
                    />
                  </Box>
                ))}
              </Box>
              <Box color="gray" mt="5px">
                Sem fornecedor, clique em avançar para buscar a NF-e pela chave
                de acesso ou pelo número.
              </Box>
            </>
          )}
          {etapa === ETAPA_NFE && (
            <Autocomplete
              freeSolo
              classes={{ option: classes.option, listbox: classes.listbox }}
              value={null}
              inputValue={chaveDigitada}
              onInputChange={(event, texto, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  setChaveDigitada(texto.trim());
                  if (previa || erro) {
                    setPrevia(null);
                    setErro('');
                  }
                }
              }}
              options={opcoesNfe}
              filterOptions={(opcoes) => opcoes}
              getOptionLabel={(opcao) =>
                typeof opcao === 'string' ? opcao : opcao.chave
              }
              renderOption={(nfe) => (
                <OpcaoNfe nfe={nfe} fornecedor={fornecedor} />
              )}
              onChange={(event, valor) => {
                if (!valor) return;
                if (typeof valor === 'string') processarTexto(valor);
                else {
                  setChaveDigitada(valor.chave);
                  buscarPrevia(valor.chave);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  variant="outlined"
                  label="Chave de acesso ou número da NF-e"
                  helperText={getAjudaChave()}
                  autoComplete="off"
                  autoFocus
                />
              )}
            />
          )}
          {erro && (
            <Box color="red" fontWeight="bold" mt="5px">
              {erro}
            </Box>
          )}
          {resumo}
          {etapa === ETAPA_ISENTO && previa && (
            <Box
              mt="16px"
              padding="16px"
              style={{
                border: `1px solid ${isento ? '#2e7d32' : '#cfcfcf'}`,
                borderRadius: 8,
                backgroundColor: isento ? '#f1f8f1' : '#fafafa',
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <SwitchInput
                    name="isento"
                    handleEnter={() => {}}
                    label={<b>Pagamento isento</b>}
                    handleKey={() => {}}
                    checked={isento}
                    onChange={(event) => {
                      if (event.target.checked) {
                        if (refPermissaoIsento.current) {
                          refPermissaoIsento.current.handleOpen();
                        }
                      } else if (refConfirmacao.current) {
                        // tirar o isento descarta a autorização das três pessoas
                        refConfirmacao.current.handleOpen(
                          'Remover isenção',
                          'Tem certeza de que deseja remover a isenção deste descarrego? Para isentar de novo será preciso autorizar outra vez.',
                          'remover-isencao',
                        );
                      }
                    }}
                  />
                  <Box color="gray" fontSize="13px" ml="48px">
                    {isento
                      ? 'Este descarrego não será cobrado no caixa.'
                      : 'Será cobrado no caixa (F1) pelo valor calculado. Para isentar, ligue a chave e autorize.'}
                  </Box>
                </Box>
                <Chip
                  size="small"
                  label={isento ? 'Isenção autorizada' : 'Cobrado no caixa'}
                  style={{
                    backgroundColor: isento ? '#2e7d32' : '#9e9e9e',
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                />
              </Box>
              {autorizacao && (
                <>
                  <Divider style={{ margin: '14px 0' }} />
                  <Box display="flex" style={{ gap: 12 }}>
                    {[
                      ['Responsável pelo recebimento', autorizacao.user],
                      ['Gerente', autorizacao.gerente],
                      ['Testemunha', autorizacao.testemunha],
                    ].map(([papel, pessoa]) => (
                      <Box
                        key={papel}
                        flex={1}
                        padding="8px 12px"
                        style={{
                          backgroundColor: 'white',
                          border: '1px solid #c8e6c9',
                          borderRadius: 6,
                        }}
                      >
                        <Box
                          fontSize="11px"
                          color="gray"
                          style={{
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          {papel}
                        </Box>
                        <Box
                          display="flex"
                          alignItems="center"
                          fontWeight="bold"
                          fontSize="16px"
                          style={{ gap: 6 }}
                        >
                          <CheckCircleIcon
                            style={{ fontSize: 18, color: '#2e7d32' }}
                          />
                          {pessoa.username}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Button
            onClick={voltar}
            color="secondary"
            variant="contained"
            tabIndex={-1}
          >
            {etapa === ETAPA_FORNECEDOR ? 'Fechar' : 'Voltar'}
          </Button>
          <Box position="relative">
            <Button
              onClick={avancar}
              color="secondary"
              variant="contained"
              tabIndex={-1}
              disabled={carregando || (etapa !== ETAPA_FORNECEDOR && !previa)}
            >
              {etapa === ETAPA_ISENTO ? 'Salvar' : 'Avançar'}
            </Button>
            {carregando && (
              <CircularProgress
                size={24}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: -12,
                  marginLeft: -12,
                }}
              />
            )}
          </Box>
        </DialogActions>
      </Dialog>
      <DialogoConfirmacao
        ref={refConfirmacao}
        handleConfirma={(codigo) => {
          if (codigo === 'fechar-dialogo') {
            fechar();
          } else {
            // remover-isencao: descarta a autorização das três pessoas
            setIsento(false);
            setAutorizacao(null);
          }
        }}
      />
      <DialogoPermissaoIsento
        ref={refPermissaoIsento}
        handleClose={() => {}}
        handleAutorizado={(dados) => {
          setIsento(true);
          setAutorizacao(dados);
        }}
      />
    </>
  );
});

export default DialogoCreate;
